/* assets/cbi-hero.js
 * cbi-hero-carousel: autorotating crossfade. No user controls by design.
 *
 * The timer is driven by setTimeout rather than setInterval so a slow frame
 * cannot queue up a backlog of transitions. Rotation pauses while the document
 * is hidden and while the element is out of the viewport, and never starts at
 * all when the user prefers reduced motion or there are fewer than two slides.
 */

class CbiHeroCarousel extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('.cbi-hero__slide'));
    this.index = 0;
    this.timer = null;
    this.visible = true;

    this.interval = parseInt(this.dataset.interval, 10);
    if (Number.isNaN(this.interval)) this.interval = 5000;

    this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.onVisibility = this.onVisibility.bind(this);
    this.onMotionChange = this.onMotionChange.bind(this);

    document.addEventListener('visibilitychange', this.onVisibility);
    if (this.motionQuery.addEventListener) {
      this.motionQuery.addEventListener('change', this.onMotionChange);
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          this.visible = entry.isIntersecting;
          this.sync();
        }
      },
      { threshold: 0 }
    );
    this.observer.observe(this);

    this.sync();
  }

  disconnectedCallback() {
    this.stop();
    if (this.observer) this.observer.disconnect();
    document.removeEventListener('visibilitychange', this.onVisibility);
    if (this.motionQuery && this.motionQuery.removeEventListener) {
      this.motionQuery.removeEventListener('change', this.onMotionChange);
    }
  }

  get canRotate() {
    return (
      this.slides.length > 1 &&
      !this.motionQuery.matches &&
      this.visible &&
      document.visibilityState !== 'hidden'
    );
  }

  sync() {
    if (this.canRotate) {
      this.start();
    } else {
      this.stop();
    }
  }

  onVisibility() {
    this.sync();
  }

  onMotionChange() {
    if (this.motionQuery.matches) {
      this.show(0);
    }
    this.sync();
  }

  start() {
    if (this.timer !== null) return;
    this.timer = window.setTimeout(() => {
      this.timer = null;
      this.show((this.index + 1) % this.slides.length);
      this.sync();
    }, this.interval);
  }

  stop() {
    if (this.timer === null) return;
    window.clearTimeout(this.timer);
    this.timer = null;
  }

  show(next) {
    if (next === this.index) return;
    const outgoing = this.slides[this.index];
    const incoming = this.slides[next];
    if (outgoing) {
      outgoing.classList.remove('is-active');
      outgoing.setAttribute('aria-hidden', 'true');
    }
    if (incoming) {
      incoming.classList.add('is-active');
      incoming.removeAttribute('aria-hidden');
    }
    this.index = next;
  }
}

if (!customElements.get('cbi-hero-carousel')) {
  customElements.define('cbi-hero-carousel', CbiHeroCarousel);
}
